import { useNavigate } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { Activity, CheckCircle2, ClipboardList, Clock, FileBarChart, Plus } from "lucide-react";
import { useState } from "react";
import { AuditsList } from "@/components/audits/AuditsList";
import { CreateAuditDialog } from "@/components/audits/CreateAuditDialog";
import { StatusReportDialog } from "@/components/audits/StatusReportDialog";
import { ProjectPipelineTable } from "@/components/projects/ProjectPipelineTable";
import { PageHeader } from "@/components/shared/page-header";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useComplianceReport } from "@/hooks/use-reports";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

interface KpiCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  subtitle?: string;
  progress?: number;
  onClick?: () => void;
}

function KpiCard({
  title,
  value,
  icon: Icon,
  iconColor,
  iconBg,
  subtitle,
  progress,
  onClick,
}: KpiCardProps) {
  return (
    <Card
      className={cn(
        "border-border/50 transition-all",
        onClick && "cursor-pointer hover:border-primary/40 hover:shadow-md",
      )}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center gap-3 pb-2">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconBg}`}>
          <Icon className={`h-4 w-4 ${iconColor}`} />
        </div>
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-bold">{value}</p>
        {progress !== undefined && (
          <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        )}
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function ComplianceKpis({ hasAuditAccess }: { hasAuditAccess: boolean }) {
  const { data: report, isLoading } = useComplianceReport();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const passRate = isLoading ? 0 : (report?.passRate ?? 0);
  const totalInScope = isLoading ? "—" : (report?.totalInScope ?? 0);
  const completedAudits = isLoading ? "—" : (report?.complete ?? 0);
  const remainingProjects = isLoading ? "—" : (report?.remainingProjects ?? 0);

  const goToAudits = hasAuditAccess
    ? (status: string) => navigate({ to: "/dashboard/audits", search: { status } })
    : undefined;

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Compliance KPIs
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Overall Percentage Completed"
          value={isLoading ? "—" : `${passRate}%`}
          progress={isLoading ? undefined : passRate}
          icon={Activity}
          iconBg="bg-emerald-500/10"
          iconColor="text-emerald-600 dark:text-emerald-400"
        />
        <KpiCard
          title={`Total Projects in Scope for ${currentYear}`}
          value={totalInScope}
          icon={ClipboardList}
          iconBg="bg-slate-500/10"
          iconColor="text-slate-600 dark:text-slate-400"
        />
        <KpiCard
          title="Completed Audits"
          value={completedAudits}
          icon={CheckCircle2}
          iconBg="bg-emerald-500/10"
          iconColor="text-emerald-600 dark:text-emerald-400"
          onClick={goToAudits ? () => goToAudits("complete") : undefined}
        />
        <KpiCard
          title="Remaining Projects"
          value={remainingProjects}
          icon={Clock}
          iconBg="bg-blue-500/10"
          iconColor="text-blue-600 dark:text-blue-400"
        />
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { data: session } = useSession();
  const [reportOpen, setReportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, 200);
  const role = session?.user?.role ?? "";

  const isStakeholder = role === "stakeholder";
  const hasAuditAccess =
    role === "admin" || role === "compliance_manager" || role === "compliance_auditor";
  const canSendReport = role === "admin" || role === "compliance_manager";
  const canCreate = role === "admin" || role === "compliance_manager";
  const showKpis = hasAuditAccess || isStakeholder;
  const showActiveAudits = hasAuditAccess;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Compliance Dashboard"
        description="Manage the audit pipeline and initiate compliance audits."
        actions={
          <>
            {canSendReport && (
              <Button variant="outline" size="sm" onClick={() => setReportOpen(true)}>
                <FileBarChart className="h-4 w-4 mr-1.5" />
                Status Report
              </Button>
            )}
            {canCreate && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" />
                Create Audit
              </Button>
            )}
          </>
        }
      />
      {showKpis && <ComplianceKpis hasAuditAccess={hasAuditAccess} />}
      {canCreate && <ProjectPipelineTable />}
      {showActiveAudits && (
        <div className="rounded-lg border border-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Active Audits</h2>
          <SearchInput
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder="Search audits…"
            className="max-w-sm"
          />
          <AuditsList statusFilter="active" searchQuery={debouncedQuery} showCreateButton={false} />
        </div>
      )}
      {canSendReport && <StatusReportDialog open={reportOpen} onOpenChange={setReportOpen} />}
      {canCreate && <CreateAuditDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </div>
  );
}
